import type { RoomLayoutSpot } from './RoomLayoutMap.types';
import { spotMatchesKey } from './spotKey';

/**
 * Shared "reserved by someone else" check for a selected spot key, used by both the single-selection
 * cleanup effect in RoomLayoutMap and the multi-slot version in {@link clearBlockedFriendSelections}
 * so the two can't drift apart.
 *
 * A reserved id matches by exact key equality or, when the spot is still in the layout, by display
 * name (see {@link spotMatchesKey}). `exemptSpotKey` is whichever key this particular selection is
 * allowed to keep despite being reserved: the single-selection effect passes the member's assigned
 * spot, and friends mode passes it only for slot 0. Every other caller passes null, so the exemption
 * never leaks to a slot that can't be the member's.
 *
 * `spot` is undefined when the key no longer resolves in the layout. Only the exact-key arm can
 * match then, and the exemption never applies since there is no spot to compare against.
 */
export function isSelectionReservedByOthers(
  spot: RoomLayoutSpot | undefined,
  key: string,
  reservedSpotKeys: string[] | undefined,
  exemptSpotKey: string | null
): boolean {
  const reserved = reservedSpotKeys ?? [];
  const isReserved = reserved.some(
    (id) => String(id) === key || (spot !== undefined && spotMatchesKey(spot, String(id)))
  );
  if (!isReserved) return false;
  const isExempt =
    spot !== undefined && exemptSpotKey !== null && spotMatchesKey(spot, exemptSpotKey);
  return !isExempt;
}
